import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { User, LogOut, Bell, Settings, Shield, Eye, PartyPopper, Megaphone, Building2, Music, ChevronDown, Check, MessageSquare } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useViewMode, ViewMode } from "@/hooks/useViewMode";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { openCreateEventModal, preloadCreateEventModal } from "@/components/CreateEventModal";
import { openSignInDialog } from "@/components/SignInDialog";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { useScrollingDown } from "@/hooks/useScrollDirection";
import { useModalOpen } from "@/hooks/useModalOpen";

type UserRole = "attendee" | "organizer" | "promoter" | null;

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { viewMode, setViewMode } = useViewMode();
  const scrollingDown = useScrollingDown();
  const modalOpen = useModalOpen();
  
  // Use centralized auth hook
  const auth = useAuth();
  const user = auth?.user ?? null;
  const profile = auth?.profile ?? null;
  const isAdmin = auth?.isAdmin ?? false;
  const authLoading = auth?.isLoading ?? true;
  
  // Derive user role from profile (primary role for backwards compat)
  const userRole: UserRole = profile?.accountType === "professional" && profile?.approvalStatus === "approved"
    ? (profile.professionalType === "event_organizer" ? "organizer" : profile.professionalType as UserRole)
    : (profile ? "attendee" : null);
  const approvalStatus = profile?.approvalStatus ?? null;
  const professionalRoles = profile?.professionalRoles ?? [];

  // Pick avatar based on active view mode (organizer uses role-specific avatar)
  const avatarUrl = (() => {
    if (!profile) return "";
    if (viewMode === "organizer") return profile.organizerAvatarUrl || profile.avatarUrl || "";
    return profile.avatarUrl || "";

  })();

  // Hide navbar on token-based check-in page (standalone scanner for staff)
  const searchParams = new URLSearchParams(location.search);
  const isStandaloneScanner = location.pathname.startsWith("/check-in/") && searchParams.has("token");

  // Fetch unread notifications count (lightweight query)
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    const fetchUnread = async () => {
      const roleContext = viewMode === "explore" ? "attendee" : viewMode;
      const { count } = await supabase
        .from("notifications")
        .select("*", { count: 'exact', head: true })
        .eq("user_id", user.id)
        .eq("is_read", false)
        .eq("role_context", roleContext);
      setUnreadCount(count || 0);
    };

    fetchUnread();

    // Subscribe to notification changes
    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        () => fetchUnread()
      )
      .subscribe();

    // Realtime may not deliver updates; pages that mark notifications read
    // also broadcast this event so the red dot clears immediately.
    window.addEventListener("notifications-updated", fetchUnread);

    return () => {
      window.removeEventListener("notifications-updated", fetchUnread);
      supabase.removeChannel(channel);
    };
  }, [user, viewMode]);

  const handleSignOut = async () => {
    // Use centralized signOut so all auth listeners update consistently
    // Don't await: mobile browsers can suspend network and leave this hanging.
    void auth?.signOut?.();
    navigate("/");
    setIsOpen(false);
  };

  // Track confirmed states to prevent UI flicker during auth state transitions
  const [confirmedAdmin, setConfirmedAdmin] = useState(false);
  const [confirmedProfessional, setConfirmedProfessional] = useState(false);
  
  // Once confirmed, remember the status to prevent flicker
  useEffect(() => {
    if (isAdmin) setConfirmedAdmin(true);
    if (!user) setConfirmedAdmin(false);
  }, [isAdmin, user]);
  
  useEffect(() => {
    const hasRole = professionalRoles.some(r => r.approvalStatus === "approved" || r.approvalStatus === "pending");
    if (hasRole || (userRole && ["organizer", "promoter"].includes(userRole) && (approvalStatus === "approved" || approvalStatus === "pending"))) {
      setConfirmedProfessional(true);
    }
    if (!user) setConfirmedProfessional(false);
  }, [userRole, approvalStatus, user, professionalRoles]);

  // Use confirmed states OR current states (confirmed prevents flicker during loading)
  const showAdminLink = confirmedAdmin || isAdmin;
  const isInExploreMode = viewMode === "explore";
  const isAlsoPromoter = profile?.isAlsoPromoter ?? false;

  // Determine available professional roles (string set, broader than ViewMode
   // since "promoter" is no longer a view mode but still gates the profile-menu link)
  const availableRoles: string[] = [];
  for (const role of professionalRoles) {
    if ((role.approvalStatus === "approved" || role.approvalStatus === "pending") && !availableRoles.includes(role.roleType)) {
      availableRoles.push(role.roleType);
    }
  }
  // Also add promoter if they have an active promoter record but no explicit role entry
  if (isAlsoPromoter && !availableRoles.includes("promoter")) {
    availableRoles.push("promoter");
  }

  const isProfessional = confirmedProfessional || availableRoles.length > 0;

  // If auth is loading AND we don't have user data, show loading state
  // But if we have a user, show UI immediately (profile may still be loading)
  const isAuthReady = !authLoading || !!user;

  // Hide navbar on token-based check-in page (standalone scanner for staff)
  if (isStandaloneScanner) {
    return null;
  }

  const isActive = (path: string) => location.pathname === path;

// Role-based navigation items - respect view mode
const getNavItems = () => {
  // If professional is in explore mode, show attendee navigation
  if (isProfessional && isInExploreMode) {
    return [
      { path: "/events", label: "Browse", gradient: "from-orange-400 to-orange-600" },
      { path: "/my-tickets", label: "Tickets", gradient: "from-cyan-500 to-blue-500" },
    ];
  }
  
  // Show nav based on active viewMode role
  if (viewMode === "organizer") {
    return [
      { path: "/create-event", label: "Create", gradient: "from-emerald-500 to-teal-500" },
      { path: "/manage", label: "Manage", gradient: "from-amber-500 to-orange-500" },
    ];
  }

  // Attendees / explore: Browse, Tickets
  return [
    { path: "/events", label: "Browse", gradient: "from-orange-400 to-orange-600" },
    { path: "/my-tickets", label: "Tickets", gradient: "from-cyan-500 to-blue-500" },
  ];
};

// Get icon for current user role in professional mode
const getProfessionalIcon = () => {
  switch (userRole) {
    case "organizer": return PartyPopper;
    case "promoter": return Megaphone;
    default: return PartyPopper;
  }
};

const getProfessionalLabel = () => {
  switch (userRole) {
    case "organizer": return "Organizer";
    case "promoter": return "Promoter";
    default: return "Professional";
  }
};

// Role config for dropdown
type RoleTheme = {
  label: string;
  icon: typeof Eye;
  /** soft colour wash spread across the whole tile */
  wash: string;
  /** colour lit from within the tile, never bleeding outside it */
  glow: string;
  /** hairline edge so the wash still reads as a tile */
  ring: string;
  /** icon + label tint */
  tint: string;
};

const roleConfig: Record<ViewMode, RoleTheme> = {
  explore: {
    label: "Attendee",
    icon: Eye,
    wash: "bg-gradient-to-br from-cyan-500/30 via-blue-500/12 to-transparent",
    glow: "shadow-[inset_0_0_20px_-4px_rgba(6,182,212,0.55)]",
    ring: "ring-1 ring-inset ring-cyan-400/25",
    tint: "text-cyan-200 hover:text-cyan-100",
  },
  organizer: {
    label: "Organizer",
    icon: PartyPopper,
    wash: "bg-gradient-to-br from-amber-500/30 via-orange-500/12 to-transparent",
    glow: "shadow-[inset_0_0_20px_-4px_rgba(245,158,11,0.55)]",
    ring: "ring-1 ring-inset ring-amber-400/25",
    tint: "text-amber-200 hover:text-amber-100",
  },
};

// Soft light pooling from the top of an active tile
const ROLE_SHEEN =
  "bg-[radial-gradient(120%_85%_at_50%_0%,rgba(255,255,255,0.12),transparent_65%)]";

// View Mode Dropdown Component
const ViewModeDropdown = () => {
  const handleSelect = (mode: ViewMode) => {
    if (mode !== viewMode) {
      setViewMode(mode);
      // Stay on profile page if already there
      if (!location.pathname.startsWith("/profile")) {
        navigate("/");
      }
    }
  };

  const currentConfig = roleConfig[viewMode] || roleConfig.explore;
  const CurrentIcon = currentConfig.icon;

  // Build dropdown options in fixed order: Attendee -> Organizer
  const roleOrder: ViewMode[] = ["explore", "organizer"];
  const dropdownRoles: ViewMode[] = roleOrder.filter(
    (r) => r === "explore" || availableRoles.includes(r)
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "relative flex items-center gap-2 px-3 h-10 md:h-auto md:py-1.5 rounded-xl md:rounded-[8px] text-xs font-medium transition-all duration-300 overflow-hidden bg-card/70 backdrop-blur-xl md:backdrop-blur-none shadow-lg shadow-black/40 md:shadow-none",
            cn(currentConfig.wash, currentConfig.glow, currentConfig.ring)
          )}
        >
          <span aria-hidden className={cn("pointer-events-none absolute inset-0", ROLE_SHEEN)} />
          <CurrentIcon
            className={cn("relative h-4 w-4 transition-colors duration-300", currentConfig.tint)}
          />
          <span
            className={cn(
              "relative hidden sm:inline transition-colors duration-300",
              currentConfig.tint
            )}
          >
            {currentConfig.label}
          </span>
          <ChevronDown className="relative h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-40">
        {dropdownRoles.map((role) => {
          const config = roleConfig[role];
          if (!config) return null;
          const RoleIcon = config.icon;
          const isActive = viewMode === role;
          return (
            <DropdownMenuItem
              key={role}
              onClick={() => handleSelect(role)}
              className="flex items-center gap-3 cursor-pointer"
            >
              <div
                className={cn(
                  "flex items-center justify-center w-7 h-7 rounded-[7px] transition-all duration-300",
                  isActive
                    ? cn(config.wash, config.glow, config.ring)
                    : "bg-muted/40 ring-1 ring-inset ring-transparent"
                )}
              >
                <RoleIcon
                  className={cn(
                    "h-3.5 w-3.5 transition-colors duration-300",
                    isActive ? config.tint : "text-muted-foreground"
                  )}
                />
              </div>
              <span className={cn("font-medium", isActive && config.tint)}>{config.label}</span>
              {isActive && <Check className="h-4 w-4 text-primary ml-auto" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};


 const navItems = user && isAuthReady ? getNavItems() : [
  { path: "/events", label: "Browse", gradient: "from-orange-400 to-orange-600" },
  { path: "/create-event", label: "Create", gradient: "from-emerald-500 to-teal-500" },
  { path: "/manage", label: "Manage", gradient: "from-amber-500 to-orange-500" },
  { path: "/my-tickets", label: "Tickets", gradient: "from-cyan-500 to-blue-500" },
  { path: "/auth", label: "Sign In", gradient: "from-pink-500 to-rose-400" },
];

// Soft light pooling from the top of the sign-in tile
const SIGNIN_SHEEN =
  "bg-[radial-gradient(120%_85%_at_50%_0%,rgba(255,255,255,0.12),transparent_65%)]";

// Pink-tinted light pooling for the sign-in tile so the highlight doesn't grey out the colour
const SIGNIN_SHEEN_PINK =
  "bg-[radial-gradient(120%_85%_at_50%_0%,rgba(255,195,222,0.18),transparent_70%)]";

const NavLinks = () => (

  <>
    {navItems.map((item: any) => {
      if (item.disabled) {
        return (
          <Button
            key={item.path}
            variant="ghost"
            disabled
            title="Pending approval"
            className="text-muted-foreground opacity-50 cursor-not-allowed"
          >
            {item.label}
          </Button>
        );
      }
      if (item.path === "/auth") {
        return (
          <Button
            key={item.path}
            variant="ghost"
            onClick={() => {
              setIsOpen(false);
              openSignInDialog();
            }}
            className={cn(
              "relative overflow-hidden rounded-xl transition-all duration-300",
              "bg-card/70 backdrop-blur-xl supports-[backdrop-filter]:bg-card/60",
              "bg-[linear-gradient(135deg,rgba(244,114,182,0.32),rgba(236,72,153,0.18),rgba(190,24,93,0.10))]",
              "shadow-[inset_0_0_22px_-5px_rgba(255,150,195,0.45)]",
              "text-brand-foreground"
            )}
          >
            <span aria-hidden className={cn("pointer-events-none absolute inset-0", SIGNIN_SHEEN_PINK)} />
            <span className="relative">{item.label}</span>
          </Button>
        );
      }

      return (
        <Link
          key={item.path}
          to={item.path}
          onClick={() => setIsOpen(false)}
        >
          <Button
            variant="ghost"
            className={cn(
              "relative overflow-hidden transition-all duration-300",
              isActive(item.path)
                ? `bg-gradient-to-r ${item.gradient} text-white hover:opacity-90`
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {item.label}
          </Button>
        </Link>
      );
    })}

    </>
  );

  return (
    <>
      <nav className={cn(
        "sticky top-0 z-50 transition-transform duration-300 pointer-events-none md:pointer-events-auto md:bg-background/95 md:backdrop-blur-xl md:supports-[backdrop-filter]:bg-background/90",
        (scrollingDown || modalOpen) && "-translate-y-full md:translate-y-0"
      )}>
        <div className="container mx-auto px-3 md:px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <Link to="/" className="pointer-events-auto flex items-center gap-2 font-bold text-xl group px-1 -mx-1">
              <span className={`text-xl md:text-2xl font-bold transition-opacity duration-300 ${isActive("/") ? "opacity-100" : "opacity-75 group-hover:opacity-100"}`}>
                Nightspy
              </span>
            </Link>
            {user && isProfessional && (
              <div className="hidden md:block">
                <ViewModeDropdown />
              </div>
            )}
          </div>


          {/* Desktop links and profile; mobile feedback and role toggle stay together. */}
          <div className="pointer-events-auto flex items-center gap-3 md:gap-4">
            <div className="hidden md:flex items-center gap-4">
              <NavLinks />
            </div>
            {user ? (


              <div className="flex items-center gap-3">
              {isProfessional && (
                <div className="md:hidden">
                  <ViewModeDropdown />
                </div>
              )}
              <Button
                variant="outline"
                aria-label="Send Feedback"
                onClick={() => setFeedbackOpen(true)}
                className={cn(
                  "md:hidden relative overflow-hidden rounded-xl h-10 w-10 transition-all duration-300",
                  "bg-background/95 backdrop-blur-xl",
                  "hover:text-brand-foreground"
                )}
              >
                <MessageSquare className="relative h-4 w-4" />
              </Button>
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button
                    data-tour="profile-menu"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "relative overflow-hidden rounded-xl h-10 w-10 backdrop-blur-xl bg-card/70 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.4)] transition-all duration-300 md:bg-transparent md:backdrop-blur-none md:shadow-none",
                      isActive("/profile") &&
                        "bg-gradient-to-br from-violet-500/30 via-indigo-500/12 to-transparent shadow-[inset_0_0_20px_-4px_rgba(139,92,246,0.55)]"
                    )}
                  >
                    {isActive("/profile") && (
                      <span aria-hidden className={cn("pointer-events-none absolute inset-0", SIGNIN_SHEEN)} />
                    )}
                    <Avatar className="relative h-8 w-8">
                      <AvatarImage src={avatarUrl} alt="Profile" />
                      <AvatarFallback>
                        <User className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                    {unreadCount > 0 && (
                      <div className="absolute top-0 right-0 h-3 w-3 bg-destructive rounded-full border-2 border-background" />
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[300px] sm:w-[350px]">
                  <div className="flex flex-col gap-2 mt-6">
                    {user && (
                      <Link to="/profile" onClick={() => setIsOpen(false)} className="pb-4 border-b mb-2 block">
                        <div className={cn(
                          "flex items-center gap-4 p-4 rounded-lg transition-colors",
                          isActive("/profile") ? "bg-primary text-primary-foreground" : "bg-muted/50 hover:bg-muted"
                        )}>
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={avatarUrl} alt="Profile" />
                            <AvatarFallback>
                              <User className="h-6 w-6" />
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-base font-medium truncate">{user.email}</p>
                            <p className={cn("text-sm", isActive("/profile") ? "opacity-80" : "text-muted-foreground")}>View Profile</p>
                          </div>
                        </div>
                      </Link>
                    )}


                    <Link to="/profile?tab=notifications" onClick={() => setIsOpen(false)}>
                      <Button variant="ghost" size="lg" className={cn("w-full justify-start text-base h-12", location.pathname === "/profile" && location.search.includes("tab=notifications") && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground")}>
                        <Bell className="h-5 w-5 mr-3" />
                        Notifications
                        {unreadCount > 0 && (
                          <span className="ml-auto bg-destructive text-destructive-foreground px-2 py-0.5 rounded-full text-xs font-medium">
                            {unreadCount}
                          </span>
                        )}
                      </Button>
                    </Link>

                    <Link to="/profile?tab=settings" onClick={() => setIsOpen(false)}>
                      <Button variant="ghost" size="lg" className={cn("w-full justify-start text-base h-12", location.pathname === "/profile" && location.search.includes("tab=settings") && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground")}>
                        <Settings className="h-5 w-5 mr-3" />
                        Settings
                      </Button>
                    </Link>

                    {(availableRoles.includes("promoter") || isAlsoPromoter) && (
                      <Link to="/promoter/manage" onClick={() => setIsOpen(false)}>
                        <Button variant="ghost" size="lg" className={cn("w-full justify-start text-base h-12", isActive("/promoter/manage") && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground")}>
                          <Megaphone className="h-5 w-5 mr-3" />
                          Promoter
                        </Button>
                      </Link>
                    )}

                    {showAdminLink && (
                      <Link to="/backlog" onClick={() => setIsOpen(false)}>
                        <Button variant="ghost" size="lg" className={cn("w-full justify-start text-base h-12", isActive("/backlog") && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground")}>
                          <Shield className="h-5 w-5 mr-3" />
                          Admin
                        </Button>
                      </Link>
                    )}


                    <div className="border-t pt-2 mt-2">
                      <Button variant="ghost" size="lg" className="w-full justify-start text-base h-12 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={handleSignOut}>
                        <LogOut className="h-5 w-5 mr-3" />
                        Sign Out
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              </div>

            ) : (
              <Button
                variant="ghost"
                className={cn(
                  "md:hidden relative overflow-hidden rounded-xl h-10 px-4",
                  "bg-card/70 backdrop-blur-xl supports-[backdrop-filter]:bg-card/60",
                  "bg-[linear-gradient(135deg,rgba(244,114,182,0.32),rgba(236,72,153,0.18),rgba(190,24,93,0.10))]",
                  "shadow-[inset_0_0_22px_-4px_rgba(255,150,195,0.5)]",
                  "text-brand-foreground"
                )}
                onClick={() => openSignInDialog()}
              >
                <span aria-hidden className={cn("pointer-events-none absolute inset-0", SIGNIN_SHEEN_PINK)} />
                <span className="relative">Sign In</span>
              </Button>

            )}
          </div>
        </div>
      </nav>
      {user && (
        <Button
          variant="outline"
          onClick={() => setFeedbackOpen(true)}
          className="hidden md:flex fixed bottom-6 right-6 z-40 gap-2 bg-background/95 backdrop-blur-xl"
        >
          <MessageSquare className="h-4 w-4" />
          Send Feedback
        </Button>
      )}
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );


};